<template>
  <v-btn
    v-bind="{ ...$attrs, ...$props, variant: 'flat' }"
    :tabindex="$props.disabled && '-1'"
    class="e-btn"
    :class="classes">
    <template v-for="(_, slot) in $slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>
    <template v-if="!props.icon" #default>
      <slot />
    </template>
    <template #loader>
      <v-progress-circular class="e-btn__loader" indeterminate size="14" width="2" />
    </template>
  </v-btn>
</template>

<script setup>
import { VBtn } from 'vuetify/components/VBtn'
import { VProgressCircular } from 'vuetify/components/VProgressCircular'

import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
  },
  icon: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})
const classes = computed(() => {
  return {
    [`e-btn--variant-${props.variant}`]: true,
  }
})
</script>

<style lang="scss">
@import '../../scss/styles';
.e-btn,
.v-btn.e-btn {
  font-weight: 600;
  letter-spacing: normal;
  &.v-btn--icon {
    &.v-btn--size-small {
      .v-icon {
        font-size: 16px;
      }
      height: 28px;
      width: 28px;
    }
    &.v-btn--size-default {
      .v-icon {
        font-size: 20px;
      }
      height: 36px;
      width: 36px;
    }
    &.v-btn--size-large {
      .v-icon {
        font-size: 24px;
      }
      height: 44px;
      width: 44px;
    }
  }
  &.v-btn--size-small,
  &.v-btn--size-large {
    font-size: 14px;
  }
  &.v-btn--loading {
    .e-btn__loader {
      > svg {
        .v-progress-circular__underlay {
          stroke: transparent;
        }
      }
    }
  }
  &.v-btn {
    border-radius: 6px;
    text-transform: initial;
    .v-btn__overlay {
      display: none;
    }
    &:after {
      content: none;
    }
  }

  // Primary
  &.e-btn--variant-primary {
    transition: background-color, border, $component-transition;
    background: $primary300;
    color: $whitebase;
    &:focus-visible {
      @include blue-ring;
    }
    &:hover {
      background: $primary400;
    }
    &.v-btn--active,
    &:active {
      background: $primary500;
    }
    &.v-btn--loading {
      background: $primary500;
    }
    &.v-btn--disabled {
      background: $grayscale100;
      color: $grayscale300;
    }

    &.v-theme--dark {
      &.v-btn--disabled {
        color: $grayscale600;
        border-color: $grayscale800;
        background: $grayscale800;
      }
    }
  }

  // Secondary
  &.e-btn--variant-secondary {
    transition: background-color, border, $component-transition;
    background: $whitebase;
    color: $primary300;
    border: 1px solid $primary300;
    &:focus-visible {
      border-color: transparent;
      @include blue-ring;
    }
    &:hover {
      background: $primary50;
      color: $primary400;
    }
    &.v-btn--active,
    &:active {
      background: $primary50;
      border-color: $primary400;
      color: $primary300;
    }
    &.v-btn--disabled {
      background: transparent;
      color: $grayscale300;
      border-color: $grayscale300;
    }
    &.v-theme--dark {
      background: $graybase;
      color: $primary300;
      &:focus-visible {
        background: $grayscale800;
      }
      &:hover {
        background: $primary700;
      }
      &.v-btn--active,
      &:active {
        background: $primary700;
        border-color: $primary400;
      }
      &.v-btn--disabled {
        color: $grayscale700;
        border-color: $grayscale700;
      }
    }
  }
  // tertiary
  &.e-btn--variant-tertiary {
    transition: background-color, border, $component-transition;
    background: $whitebase;
    color: $graybase;
    border: 1px solid $grayscale300;

    &:focus-visible {
      border-color: $graybase;
      @include gray-ring;
    }
    &:hover {
      background: $grayscale200;
      border-color: $grayscale200;
    }
    &.v-btn--active,
    &:active {
      background: $grayscale100;
      border-color: $grayscale100;
    }
    &.v-btn--disabled {
      background: transparent;
      color: $grayscale300;
      border-color: $grayscale300;
    }

    &.v-theme--dark {
      background: $graybase;
      color: $whitebase;
      border-color: $grayscale700;
      &:focus-visible {
        border-color: $grayscale700;
      }
      &:hover,
      &.v-btn--active {
        background: $grayscale700;
        border-color: $grayscale700;
      }
      &.v-btn--disabled {
        color: $grayscale700;
        border-color: $grayscale700;
      }
    }
  }
  // Ghost
  &.e-btn--variant-ghost {
    transition: background-color, border, $component-transition;
    background: transparent;
    color: $grayscale500;
    &:focus-visible {
      @include gray-ring;
    }
    &:hover {
      color: $graybase;
      background: $grayscale200;
    }
    &.v-btn--active,
    &:active {
      color: $graybase;
      background: $grayscale100;
    }
    &.v-btn--disabled {
      background: transparent;
      color: $grayscale300;
    }

    &.v-theme--dark {
      color: $grayscale400;

      &:focus-visible {
        color: $whitebase;
      }
      &:hover,
      &.v-btn--active {
        color: $whitebase;
        background: $grayscale700;
      }
      &.v-btn--disabled {
        color: $grayscale700;
        border-color: $grayscale700;
      }
    }
  }

  // danger
  &.e-btn--variant-danger {
    transition: background-color, border, $component-transition;
    background: $whitebase;
    color: $danger200;
    border: 1px solid $danger200;

    &:hover {
      background: $grayscale50;
    }
    &.v-btn--active,
    &:active {
      background: $danger50;
      border-color: $danger300;
    }
    &:focus-visible {
      border-color: $danger300;
      @include red-ring;
    }
    &.v-btn--loading {
      border-color: $danger400;
      color: $danger400;
    }
    &.v-btn--disabled {
      background: transparent;
      color: $grayscale300;
      border-color: $grayscale300;
    }
    &.v-theme--dark {
      background: $graybase;
      &:hover,
      &.v-btn--active {
        background: $grayscale800;
      }
      &.v-btn--active,
      &:active {
        background: $danger400;
        border-color: $danger300;
      }
      &.v-btn--disabled {
        background: transparent;
        color: $grayscale700;
        border-color: $grayscale700;
      }
    }
  }

  // destroy
  &.e-btn--variant-destroy {
    transition: background-color, border, $component-transition;
    background: $danger200;
    border: 1px solid $danger200;
    color: $whitebase;

    &:focus-visible {
      border-color: $danger200;
      @include red-ring;
    }
    &:hover {
      background: $danger300;
      border-color: $danger300;
    }
    &.v-btn--active,
    &:active {
      background: $danger400;
      border-color: $danger400;
    }
    &.v-btn--loading {
      color: $danger400;
    }
    &.v-btn--disabled {
      background: transparent;
      color: $grayscale300;
      border-color: $grayscale300;
    }
    &.v-theme--dark {
      &.v-btn--disabled {
        background: transparent;
        color: $grayscale700;
        border-color: $grayscale700;
      }
    }
  }

  // FAB
  &.e-btn--variant-fab {
    transition: background-color, border, $component-transition;
    background: $primary300;
    border-radius: 50%;
    color: $whitebase;
    &:focus-visible {
      @include blue-ring;
    }
    &:hover {
      background: $primary400;
    }
    &.v-btn--active,
    &:active {
      background: $primary500;
    }
    &.v-btn--loading {
      background: $primary500;
    }
    &.v-btn--disabled {
      background: $grayscale100;
      color: $grayscale300;
    }
    &.v-theme--dark {
      &.v-btn--disabled {
        color: $grayscale600;
        background: $grayscale800;
      }
    }
  }

  // link
  &.e-btn--variant-link {
    transition: background-color, border, $component-transition;
    border-radius: initial;
    height: initial;
    padding: 0;
    min-width: initial;
    color: $primary300;
    text-decoration: underline;
    text-underline-position: under;
    font-weight: initial;
    background: transparent;
    &.v-btn--size-small {
      font-size: 14px;
    }
    &.v-btn--size-default {
      font-size: 16px;
    }
    &.v-btn--size-large {
      font-size: 18px;
    }
    &:focus-visible {
      text-decoration: none;
    }
    &:hover {
      color: $primary400;
      text-decoration: none;
    }
    &.v-btn--active,
    &:active {
      color: $primary400;
      text-decoration: underline;
    }
    &.v-btn--disabled {
      color: $grayscale300;
    }
    &.v-theme--dark {
      &.v-btn--disabled {
        color: $grayscale700;
      }
    }
  }
}
</style>
